'use client'

import React, { useState } from 'react'

import { ArrowRight, File } from 'lucide-react'
import { twMerge } from 'tailwind-merge'

// ============================================================================
// Types
// ============================================================================

interface ViewDocumentsButtonProps {
  /** Number of files/attachments to display */
  fileCount: number
  /** Callback when button is clicked (typically scrolls to documents section) */
  onClick: () => void
  /** Optional custom label (default: "View Documents") */
  label?: string
  /** Optional custom subtitle (default: "Access {fileCount} attachments") */
  subtitle?: string
  /** Optional className for the button wrapper */
  className?: string
}

// ============================================================================
// Component
// ============================================================================

/**
 * ViewDocumentsButton - Sidebar button to scroll to documents section
 *
 * Used across all opportunity detail pages to provide quick access
 * to the documents section with file count preview.
 *
 * @example
 * ```tsx
 * <ViewDocumentsButton
 *   fileCount={data.files.length}
 *   onClick={scrollToFiles}
 * />
 * ```
 */
const ViewDocumentsButton: React.FC<ViewDocumentsButtonProps> = ({
  fileCount,
  onClick,
  label = 'View Documents',
  subtitle,
  className,
}) => {
  const [isShaking, setIsShaking] = useState(false)
  const hasDocuments = fileCount > 0

  const handleClick = () => {
    if (hasDocuments) {
      onClick()
    } else {
      setIsShaking(true)
      setTimeout(() => setIsShaking(false), 300)
    }
  }

  const displayLabel = hasDocuments ? label : 'No Documents'
  const displaySubtitle = hasDocuments
    ? (subtitle ?? `Access ${fileCount} attachments`)
    : 'No attachments available'

  return (
    <button
      onClick={handleClick}
      className={twMerge(
        'group flex w-full items-center justify-between rounded-lg border border-primary-200 bg-white p-3.5 text-left shadow-sm transition-all dark:border-primary-700 dark:bg-primary-900',
        hasDocuments &&
          'hover:border-accent-300 hover:shadow-md dark:hover:border-accent-600',
        isShaking && 'animate-shake border-red-300 dark:border-red-500',
        className,
      )}
    >
      <div className="flex items-center gap-3">
        <div
          className={twMerge(
            'flex h-8 w-8 items-center justify-center rounded-full border transition-colors',
            hasDocuments &&
              'border-primary-200 bg-primary-100 text-primary-600 group-hover:bg-primary-200 dark:border-primary-700 dark:bg-primary-900/50 dark:text-primary-400 dark:group-hover:bg-primary-800',
            !hasDocuments &&
              'border-primary-200 bg-primary-50 text-primary-400 dark:border-primary-700 dark:bg-primary-800 dark:text-primary-500',
          )}
        >
          <File className="h-4 w-4" />
        </div>
        <div className="flex flex-col gap-1">
          <span
            className={twMerge(
              'block text-sm font-bold',
              hasDocuments && 'text-primary-900 dark:text-primary-100',
              !hasDocuments && 'text-primary-600 dark:text-primary-400',
            )}
          >
            {displayLabel}
          </span>
          <span className="text-sm text-primary-500 dark:text-primary-400">
            {displaySubtitle}
          </span>
        </div>
      </div>
      {hasDocuments && (
        <ArrowRight className="h-4 w-4 text-primary-400 transition-colors group-hover:text-accent-600 dark:group-hover:text-accent-400" />
      )}
    </button>
  )
}

ViewDocumentsButton.displayName = 'ViewDocumentsButton'

export { ViewDocumentsButton }
export type { ViewDocumentsButtonProps }
